// src/hooks/usePolarionTeamsContext.tsx
/* eslint-disable unicorn/filename-case */
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { app, dialog } from "@microsoft/teams-js";
import { QueryClient } from "react-query";
import {
  AuthCallResponse,
  IAuthCallResponse,
  TeamsContextType,
} from "src/types/CommonTypes";
import { Configuration } from "src/services/configuration";
import { Constants } from "../common/constants";
import { useLocalStorage } from "./useStorage";
import config from "../config/config";
import * as microsoftTeams from "@microsoft/teams-js";
import { LocalizationService } from "../services/TcTeamsAppLocaleService";
import { RequestUtils } from "../services/RequestUtils";
import { ApiResponse } from "../types/ApiRequest";
import { logger } from "src/Logger";

import {
  PolarionTeamsContext,
  IPolarionTeamsContextValue,
  IPolarionContextValue,
} from "../components/PolarionTeamsContext";

export const usePolarionTeamsContext = (
  teamsContext: TeamsContextType,
  queryClient: QueryClient
): IPolarionTeamsContextValue => {
  logger.logInformation("usePolarionTeamsContext() called.");

  const [isLoginFromBot, setIsLoginFromBot] = useState<boolean>(false);
  const [isPolarionX, setIsPolarionX] = useState<boolean>(false);
  const [conversationId, setConversationId] = useState<string | undefined>(
    undefined
  );
  const [tokenSid, setTokenSid] = useState<string | undefined>(undefined);
  const [taskDetailOpened, setTaskDetailOpened] = useState<boolean>(false);
  const [themeString, setThemeString] = useState<string>("default");
  const [context, setContext] = useState<app.Context | undefined>(undefined);
  const [i18n, setI18n] = useState<{ [key: string]: string }>({});

  const [polarionCode, setPolarionCode] = useLocalStorage<
    AuthCallResponse | undefined
  >(Constants.cachePolarionCode, undefined);

  const [error, setError] = useState<
    | {
        title: string;
        message: string;
        statusCode: number;
        correlationId: string;
      }
    | undefined
  >(undefined);
  const failure = { setError, error };

  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    polarionCode !== undefined && !!polarionCode.sessionId
  );

  const [configuration, setConfiguration] = useLocalStorage<any>(
    Constants.cacheConfiguration,
    undefined
  );
  const [userConfiguration, setUserConfiguration] = useLocalStorage<any>(
    Constants.cacheUserConfiguration,
    undefined
  );
  const [teamcenterLanguage, setTeamcenterLanguage] = useLocalStorage<
    string | undefined
  >(Constants.cacheTeamcenterLanguage, undefined);

  const [siteDisplay, setSiteDisplay] = useState<string>("");

  const handleIsLoginFromBot = useCallback((taskDetails: string) => {
    const [
      capabilityName,
      capabilityId,
      task_type,
      task_value,
      taskId,
      taskIdValue,
      tokenid,
      tokenSidValue,
    ] = taskDetails.split("|");
    if (task_type === "isLoginFromBot") {
      const isLoginFromBotValue = task_value === "true";
      setIsLoginFromBot(isLoginFromBotValue);
      if (taskId === "conversationId") {
        setConversationId(taskIdValue);
      }
      if (tokenid === "sid") {
        setTokenSid(tokenSidValue);
      }
      logger.logInformation(
        `isLoginFromBot set to ${isLoginFromBotValue} with ${taskId}`
      );
    }
  }, []);

  const handleRedirection = (): void => {
    let appId = config.appId;
    if (isLoginFromBot) {
      const botUrl = `https://teams.microsoft.com/l/entity/${appId}/conversations`;
      microsoftTeams.app.openLink(botUrl);
    }
    logger.logInformation(
      `Successfully handled redirection with isLoginFromBot: ${JSON.stringify(isLoginFromBot)}`
    );
  };

  // localization handler
  const localizationHandler = useCallback(
    async (locale: string) => {
      logger.logTrace(`Localization handler called with locale: ${locale}`);
      const teamcenterLang = LocalizationService.getTeamcenterLanguage(locale);
      const cachedLanguage = teamcenterLanguage ? teamcenterLanguage : "en_US";
      if (cachedLanguage !== teamcenterLang) {
        logger.logWarning(
          "Language changed, clearing cache and reinitializing state."
        );
        queryClient.clear();
        setIsAuthenticated(false);
        setIsAuthenticating(false);
        window.localStorage.clear();
      }
      setTeamcenterLanguage(teamcenterLang);
      const i18nMessages =
        await LocalizationService.getMessages(teamcenterLang);
      setI18n(i18nMessages);
      logger.logInformation(`Localization initialized for ${teamcenterLang}`);
    },
    [queryClient, setTeamcenterLanguage, teamcenterLanguage]
  );

  const openDataPrivacyDialog = useCallback(() => {
    const taskInfo = {
      title: i18n.DataPrivacyText,
      url: window.location.origin + "/index.html#/dataprivacyinitial",
      card: null,
      size: { height: 600, width: window.innerWidth > 1000 ? 450 : 350 },
    };
    dialog.url.open(taskInfo, () => queryClient.invalidateQueries());
    logger.logInformation("Opened data privacy dialog.");
  }, [i18n.DataPrivacyText, queryClient]);

  const extractAnalyticsInfo = (analyticsResponse: ApiResponse) => {
    let retVal: any = undefined;
    if (analyticsResponse.error) {
      logger.logError(
        `Analytics API error: ${JSON.stringify(analyticsResponse.error)}`
      );
      failure.setError(analyticsResponse.error);
    } else if (analyticsResponse.data) {
      retVal = {
        userId: analyticsResponse.data.userId,
        customerId: analyticsResponse.data.customerId,
        tcServerVersion: analyticsResponse.data.tcServerVersion,
        tcPlatformLocale: analyticsResponse.data.tcPlatformLocale,
      };
      logger.logTrace("Analytics info extracted successfully.");
    } else {
      const errMsg =
        "The application could not get analytics info from server.";
      logger.logError(errMsg);
      failure.setError({
        correlationId: "",
        message: errMsg,
        statusCode: 450,
        title: "Failed to get analytics info.",
      });
    }
    return retVal;
  };

  useEffect(() => {
    if (teamsContext?.context?.app?.locale) {
      logger.logTrace(
        "Locale change detected, triggering localizationHandler."
      );
      localizationHandler(teamsContext.context.app.locale);
    }
  }, [teamsContext?.context?.app?.locale, localizationHandler]);

  const polarionContextValue: IPolarionContextValue = useMemo(
    () => ({
      session: polarionCode,
      setSession: setPolarionCode,
      isAuthenticated,
      setIsAuthenticated,
      isAuthenticating,
      isPolarionX,
      setIsPolarionX,
      logout: () => {
        logger.logInformation("User logging out from Polarion context.");
        queryClient.clear();
        setIsAuthenticated(false);
        setIsAuthenticating(false);
        window.localStorage.removeItem(Constants.cachePolarionCode);
        window.localStorage.removeItem(Constants.cacheConfiguration);
        window.localStorage.removeItem(Constants.cacheUserConfiguration);
        window.localStorage.removeItem(Constants.cacheTeamcenterLanguage);
        setConfiguration(undefined);
        setUserConfiguration(undefined);
        setPolarionCode(undefined);
        setTeamcenterLanguage(undefined);
      },
      login: async () => {
        logger.logInformation("Starting Polarion login...");
        if (!teamsContext) {
          logger.logError("teamsContext is null");
          failure.setError({
            title: "Internal Error",
            message: "Teams context not available",
            statusCode: 500,
            correlationId: "",
          });
          return;
        }
        const selectedSite = Configuration.getValueFromLocalStorage(
          Constants.cacheSelectedSite
        );
        logger.logTrace(
          `Selected site for Polarion login: ${JSON.stringify(selectedSite)}`
        );
        const configurationResponse: any = await RequestUtils.callTcTeamsApi(
          Constants.opConfiguration,
          undefined
        );
        logger.logTrace(
          `Configuration response: ${JSON.stringify(configurationResponse)}`
        );

        if (configurationResponse.error) {
          logger.logError(
            `Configuration API error: ${JSON.stringify(configurationResponse.error)}`
          );
          failure.setError(configurationResponse.error);
          return;
        }

        if (configurationResponse.data) {
          setConfiguration(configurationResponse.data);
          setIsPolarionX(
            configurationResponse.data.authorizerType ===
              Constants.NonSAMAuthentication
              ? false
              : true
          );
          logger.logInformation(`isPolarionX: ${isPolarionX}`);
        }

        if (!isPolarionX) {
          if (!teamsContext.msalInstance) {
            const msg =
              "Internal Teams SDK Error: Failed to fetch user credentials";
            logger.logError(msg);
            failure.setError({
              correlationId: "",
              message: msg,
              statusCode: 500,
              title: "Failed to fetch Teams Credential",
            });
            setIsAuthenticating(false);
            return;
          }

          logger.logInformation("Using Teams SSO for Polarion login...");
          setIsAuthenticating(true);
          const authScope =
            selectedSite && selectedSite.siteId
              ? `${configurationResponse.data.azureClientId.trim()}/access_as_user`
              : "";
          logger.logTrace(`Using auth scope: ${authScope}`);
          try {
            setContext(context);
            let token: string | undefined;

            try {
              const popupResult =
                await teamsContext.msalInstance.acquireTokenPopup({
                  scopes: [authScope, "User.Read"],
                });
              token = popupResult?.accessToken;
              logger.logInformation("Token acquired successfully.");
            } catch (err) {
              logger.logError(
                "Teams SSO login failed. Please check Teams login state."
              );
              failure.setError({
                title: "Login failed",
                message:
                  "Teams Desktop only supports silent SSO. Please ensure you are logged into Teams and retry.",
                statusCode: 500,
                correlationId: "",
              });
              return;
            }

            logger.logInformation(
              "Logging into Polarion with Teams SSO token..."
            );
            const loginRequest = { TeamsSSOAccessToken: token };
            logger.logTrace(
              `Polarion login request: ${JSON.stringify(loginRequest)}`
            );

            const response = await RequestUtils.callTcTeamsApi(
              Constants.opPolarionAuth,
              undefined,
              loginRequest
            );

            logger.logTrace(
              `Polarion login response: ${JSON.stringify(response)}`
            );

            if (response?.data?.resultStatus?.httpStatusCode === 200) {
              const incomingData = response.data;
              const transformedPolarionCode: AuthCallResponse = {
                sessionId: incomingData.PolarionSessionToken,
                userId: incomingData.PolarionUserId,
                userUid: teamsContext.userInfo?.localAccountId || "",
                locale: incomingData.locale,
                tenantId: incomingData.tenantId,
                siteId: incomingData.siteId,
                appDeploymentId: incomingData.appDeploymentId,
                accessToken: incomingData.token?.accessToken,
                refreshToken: incomingData.token?.refreshToken,
                userEmailId: teamsContext?.context?.user?.loginHint || "",
                userDisplayName: teamsContext?.userInfo?.name || "",
                ssoToken: undefined,
                aadObjectId: teamsContext?.userInfo?.localAccountId || "",
                error: undefined,
              };
              setPolarionCode(transformedPolarionCode);
              setIsAuthenticated(true);
              logger.logInformation("Polarion login successful.");
            } else {
              const statusCode = response?.data?.resultStatus?.httpStatusCode;
              const statusText =
                response?.data?.resultStatus?.statusText || "Login failed";
              if (statusCode === 401) {
                logger.logWarning(
                  "Unauthorized user attempted Polarion login."
                );
                failure.setError({
                  correlationId: "",
                  message:
                    "Not an authorized user in the Polarion instance. Please create an account first.",
                  statusCode: 401,
                  title: "Could not login to Polarion",
                });
              } else {
                logger.logError(
                  `Polarion login failed: ${statusText} (${statusCode})`
                );
                failure.setError({
                  correlationId: "",
                  message: `${statusText}`,
                  statusCode: statusCode as number,
                  title: "Failed to login to Polarion",
                });
                setIsAuthenticated(false);
              }
            }
          } catch (error) {
            logger.logCritical(
              `Unexpected error during login: ${(error as Error).message}`
            );
            setIsAuthenticating(false);
          }
        }
      },
      setLogin: async (polarionCode: IAuthCallResponse) => {
        setPolarionCode(polarionCode);
        setIsAuthenticated(true);
        logger.logInformation(
          "Polarion session manually set (setLogin called)."
        );
      },
      siteDisplay,
    }),
    [
      polarionCode,
      setPolarionCode,
      isAuthenticated,
      setIsAuthenticated,
      isAuthenticating,
      isPolarionX,
      setIsPolarionX,
      queryClient,
      setConfiguration,
      setUserConfiguration,
      setTeamcenterLanguage,
      failure,
      teamsContext.msalInstance,
      siteDisplay,
      context,
    ]
  );

  logger.logInformation("usePolarionTeamsContext() completed.");
  return {
    configuration,
    isLoginFromBot,
    userConfiguration,
    teamcenterLanguage,
    i18n,
    polarion: polarionContextValue,
    failure: { error, setError },
    queryClient,
  };
};

// Hook consumer
export function usePolarionTeamsContextValue() {
  const context = useContext(PolarionTeamsContext);
  if (context === undefined) {
    throw new Error(
      "usePolarionTeamsContextValue must be used within a PolarionTeamsContextProvider"
    );
  }
  return context;
}
